import { gql, useSubscription } from '@apollo/client'
import { createContext, useContext, useMemo } from 'react'

import logger from 'src/utils/logger'

/**
 * @typedef {Object} Ctx
 * @property {boolean} hasOrgSize
 * @property {boolean} loading
 */

/** @type {ReturnType<typeof createContext<Ctx|null>>} */
const OrgSizeContext = createContext(null)

OrgSizeContext.displayName = 'OrgSizeContext'

const ORG_SIZE = gql`
    subscription ORG_SIZE {
        organizations: users_organization {
            org_size_id
            size
        }
    }
`

const OrgSizeProvider = ({ children }) => {
    const { data, loading, error } = useSubscription(ORG_SIZE)

    /** @type {Ctx} */
    const ctx = useMemo(() => {
        if (loading) {
            return { loading, hasOrgSize: null }
        }

        if (error) {
            logger.error(error)
            return { loading: false, hasOrgSize: null }
        }

        if (!data.organizations || data.organizations.length === 0) {
            return { loading: false, hasOrgSize: null }
        }

        return {
            loading: false,
            hasOrgSize: !!data.organizations[0].org_size_id,
        }
    }, [data, error, loading])

    return (
        <OrgSizeContext.Provider value={ctx}>
            {children}
        </OrgSizeContext.Provider>
    )
}

function useOrgSize() {
    const ctx = useContext(OrgSizeContext)

    if (!ctx) {
        throw new Error('Please wrap in OrgSizeContext.Provider')
    }

    return ctx
}

export { OrgSizeProvider, useOrgSize }
